import { useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Film, ImagePlus, Loader2, Sparkles, Star, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useList } from "@/components/kit";
import { API, api, errMsg } from "@/lib/api";

const SEDI = ["AFRODITE BOUTIQUE HOTEL", "TENUTA LEDO", "CATERING PRESSO SEDE ESTERNA"];
export const imgUrl = (id) => `${API}/public/gallery/${id}`;

export function GalleryManager() {
  const [sede, setSede] = useState(SEDI[0]);
  const [rows, reload] = useList(`/gallery?sede=${encodeURIComponent(sede)}`);
  const [busy, setBusy] = useState(false);
  const [enh, setEnh] = useState("");
  const ref = useRef(null);
  const vref = useRef(null);
  const uploadReel = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("sede", sede);
    try {
      await api.post("/gallery/video", fd);
      toast.success("Reel caricato");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
    vref.current.value = "";
    reload();
  };
  const star = async (g) => {
    await api.patch(`/gallery/${g.id}`, { preferita: !g.preferita });
    reload();
  };
  const enhance = async (g) => {
    setEnh(g.id);
    try {
      await api.post(`/gallery/${g.id}/enhance`);
      toast.success("Foto migliorata aggiunta alla galleria");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setEnh("");
  };
  const upload = async (files) => {
    setBusy(true);
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("sede", sede);
      fd.append("titolo", file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " "));
      try {
        await api.post("/gallery", fd);
      } catch (e) {
        toast.error(`${file.name}: ${errMsg(e)}`);
      }
    }
    setBusy(false);
    ref.current.value = "";
    reload();
  };
  const rename = async (g, titolo) => {
    await api.patch(`/gallery/${g.id}`, { titolo });
  };
  const remove = async (g) => {
    if (!window.confirm("Eliminare questa foto?")) return;
    await api.delete(`/gallery/${g.id}`);
    reload();
  };
  return (
    <div className="space-y-4" data-testid="gallery-manager">
      <p className="text-sm text-[#47606B] max-w-2xl">Le foto della sala compaiono da sole nel preventivo online, nell'area cliente e nella brochure. Con la stella scegliete le più belle (usate per prime e, se la sede non è decisa, mescolate tra Afrodite e Tenuta Ledo). Il reel va in copertina della brochure.</p>
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap gap-1.5">{SEDI.map((s, i) => <button key={s} onClick={() => setSede(s)} data-testid={`gallery-sede-${i}`} className={`text-xs px-3 py-1.5 rounded-full border ${sede === s ? "bg-[#0D2B3A] text-white border-[#0D2B3A]" : "bg-white border-[#D4E6EC]"}`}>{s}</button>)}</div>
        <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="gallery-file-input" />
        <input ref={vref} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => uploadReel(e.target.files[0])} data-testid="gallery-video-input" />
        <div className="flex gap-2"><Button variant="outline" className="rounded-full" disabled={busy} onClick={() => vref.current.click()} data-testid="gallery-reel-button"><Film size={15} className="mr-2" /> Carica reel</Button>
        <Button className="rounded-full" disabled={busy} onClick={() => ref.current.click()} data-testid="gallery-upload-button">{busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : <ImagePlus size={15} className="mr-2" />} Carica foto</Button></div>
      </div>
      {rows && rows.length === 0 && <div className="text-sm text-[#7B929C] py-6" data-testid="gallery-empty">Nessuna foto per {sede}.</div>}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {(rows || []).map((g) => (
          <div key={g.id} className="group rounded-xl overflow-hidden border border-[#D4E6EC] bg-white" data-testid={`gallery-item-${g.id}`}>
            <div className="relative aspect-[4/3] overflow-hidden">
              {g.kind === "video" ? <video src={imgUrl(g.id)} muted loop playsInline autoPlay className="w-full h-full object-cover" /> : <img src={imgUrl(g.id)} alt={g.titolo} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
              <button onClick={() => star(g)} title="Tra le più belle (usata per prima)" className={`absolute top-2 left-2 h-8 w-8 rounded-full bg-white/90 flex items-center justify-center ${g.preferita ? "text-[#E0A100]" : "text-[#7B929C]"}`} data-testid={`gallery-star-${g.id}`}><Star size={14} fill={g.preferita ? "currentColor" : "none"} /></button>
              {g.kind !== "video" && <button onClick={() => enhance(g)} disabled={!!enh} title="Migliora con IA" className="absolute bottom-2 right-2 rounded-full bg-white/90 text-[#0B6E8F] text-[11px] px-2.5 py-1 flex items-center gap-1" data-testid={`gallery-enhance-${g.id}`}>{enh === g.id ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Migliora con IA</button>}
              <button onClick={() => remove(g)} className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 text-[#991B1B] flex items-center justify-center md:opacity-0 md:group-hover:opacity-100 transition-opacity" data-testid={`gallery-delete-${g.id}`}><Trash2 size={14} /></button>
            </div>
            <input defaultValue={g.titolo} onBlur={(e) => rename(g, e.target.value)} className="w-full text-xs px-3 py-2 focus:outline-none" placeholder="Titolo" data-testid={`gallery-title-${g.id}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function GalleryStrip({ items, sede }) {
  const [open, setOpen] = useState(-1);
  if (!items || !items.length) return null;
  const go = (d) => setOpen((open + d + items.length) % items.length);
  return (
    <section className="space-y-3 pt-6" data-testid="gallery-strip">
      <h2 className="font-serif-it text-2xl font-semibold">La location{sede ? ` • ${sede}` : ""}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {items.slice(0, 8).map((g, i) => (
          <button key={g.id} onClick={() => setOpen(i)} className={`relative overflow-hidden rounded-xl ${i === 0 && items.length >= 5 ? "col-span-2 row-span-2 aspect-square md:aspect-auto" : "aspect-[4/3]"}`} data-testid={`gallery-thumb-${i}`}>
            <img src={imgUrl(g.id)} alt={g.titolo} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
          </button>
        ))}
      </div>
      {open >= 0 && (
        <div className="fixed inset-0 z-50 bg-[#0D2B3A]/95 flex items-center justify-center p-4" onClick={() => setOpen(-1)} data-testid="gallery-lightbox">
          <button className="absolute top-4 right-4 text-white" onClick={() => setOpen(-1)} data-testid="gallery-close"><X size={26} /></button>
          <button className="absolute left-3 text-white p-2" onClick={(e) => { e.stopPropagation(); go(-1); }} data-testid="gallery-prev"><ChevronLeft size={30} /></button>
          <figure onClick={(e) => e.stopPropagation()} className="max-w-5xl w-full">
            <img src={imgUrl(items[open].id)} alt={items[open].titolo} className="max-h-[80vh] w-full object-contain rounded-lg" />
            {items[open].titolo && <figcaption className="text-center text-white/80 text-sm mt-3">{items[open].titolo}</figcaption>}
          </figure>
          <button className="absolute right-3 text-white p-2" onClick={(e) => { e.stopPropagation(); go(1); }} data-testid="gallery-next"><ChevronRight size={30} /></button>
        </div>
      )}
    </section>
  );
}
